import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Loader from '../../../core/components/Loader';
import { FilterSection } from '../../../core/components/FilterSection';
import { Drawer } from '../../../core/components/Drawer';
import { fetchEmployees } from '../../employees/actions';
import { selectEmployees } from '../../employees/reducer';
import { TASK_DRAWER_WIDTH } from '../../tasks/types';
import {
  TaskOccurrence,
  useGetTaskOccurrencesQuery,
  useGetTaskTemplateQuery,
  useRunOccurrenceSchedulerMutation,
} from '../../../core/api/rtk/taskTemplatesApi';
import TaskOccurrenceCard from '../components/TaskOccurrenceCard';
import { TaskDetails } from '../components/TaskDetails';
import HourlyChecklist, { HourlyViewMode } from '../../tasks/components/HourlyChecklist';
import { buildLastFourHoursOccurrences } from '../helpers';

const TasksV2Daily = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const employees = useSelector(selectEmployees);

  const [selectedOccurrence, setSelectedOccurrence] = useState<TaskOccurrence | null>(null);
  // HourlyChecklist promotes to Default view on >1000px viewports.
  const [hourlyViewMode, setHourlyViewMode] = useState<HourlyViewMode>(HourlyViewMode.Collapsed);

  const dateRange = useMemo(() => {
    return {
      fromDate: moment().startOf('day').toISOString(),
      untilDate: moment().endOf('day').toISOString(),
    };
  }, []);

  const queryArgs = useMemo(() => ({ ...dateRange, pageSize: 500 }), [dateRange]);

  const { data, isLoading, refetch } = useGetTaskOccurrencesQuery(queryArgs);
  const parentTaskId = selectedOccurrence?.taskId ?? null;
  const { data: selectedTemplate } = useGetTaskTemplateQuery(parentTaskId as number, {
    skip: !parentTaskId,
  });

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  const occurrences: TaskOccurrence[] = data?.results || [];

  const hasInteriorTasks = (list: TaskOccurrence[]): boolean =>
    list.some((o) => (o.task as any)?.type === 'ExpressInterior' || o.type === 'ExpressInterior');

  const isTaskWithType = (o: TaskOccurrence, type: string): boolean =>
    ((o.task as any)?.type || o.type) === type;

  const filterDaily = (list: TaskOccurrence[]): TaskOccurrence[] =>
    list.filter((o) => {
      if ((o.task as any)?.interval === 'Hourly') return false;
      if (o.status !== 'Completed') return true;
      const completedTime = o.completedAt ?? o.timestamp;
      return completedTime != null && completedTime >= dateRange.fromDate;
    });

  const filterHourly = (list: TaskOccurrence[]): TaskOccurrence[] =>
    list.filter((o) => (o.task as any)?.interval === 'Hourly');

  const sortByName = (a: TaskOccurrence, b: TaskOccurrence): number => {
    const nameA = a.task?.name || a.name || '';
    const nameB = b.task?.name || b.name || '';
    return nameA.localeCompare(nameB);
  };

  const sortByCompleted = (a: TaskOccurrence, b: TaskOccurrence): number => {
    const ta = new Date(a.completedAt ?? a.timestamp ?? '').getTime();
    const tb = new Date(b.completedAt ?? b.timestamp ?? '').getTime();
    return tb - ta;
  };

  // Signature matches legacy `filterComplete(list, invert?)` so HourlyChecklist
  // can reuse it with invert=true for the incomplete-only view.
  const filterComplete = (list: TaskOccurrence[], invert = false): TaskOccurrence[] => {
    if (invert) {
      return list.filter((o) => o.status !== 'Completed');
    }
    return list.filter((o) => o.status === 'Completed').sort(sortByCompleted);
  };

  const dailyOccurrences = useMemo(
    () => filterDaily(occurrences),
    [occurrences, dateRange.fromDate]
  );
  const hourlyOccurrences = useMemo(() => filterHourly(occurrences), [occurrences]);
  const lastFourHoursTasks = useMemo(
    () => buildLastFourHoursOccurrences(hourlyOccurrences),
    [hourlyOccurrences]
  );

  const [runScheduler, { isLoading: isScheduling }] = useRunOccurrenceSchedulerMutation();

  const handleRunScheduler = async () => {
    try {
      await runScheduler().unwrap();
      refetch();
    } catch {
      // surfaced via RTK Query error state on the mutation hook
    }
  };

  const renderTask = (occurrence: TaskOccurrence) => (
    <TaskOccurrenceCard
      key={occurrence.id}
      occurrence={occurrence}
      onOpen={(occ) => setSelectedOccurrence(occ)}
      className="tabular"
    />
  );

  return (
    <div className="todays-tasks-new">
      {employees && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {/* {process.env.NODE_ENV === 'development' && (
              <button className="button mb-2" onClick={handleRunScheduler} disabled={isScheduling}>
                {isScheduling ? 'Running…' : 'Run scheduler'}
              </button>
            )} */}
            {hourlyViewMode === HourlyViewMode.Collapsed && (
              <button
                className="task-drawer-toggle-button"
                onClick={() => setHourlyViewMode(HourlyViewMode.Default)}
              >
                <i className="uil uil-angle-left" />
              </button>
            )}
          </div>
          <div
            className={`tasks-columns${
              hourlyViewMode === HourlyViewMode.Collapsed ? ' hourly-collapsed' : ''
            }`}
          >
            <div className="tasks-daily">
              {isLoading ? (
                <Loader />
              ) : (
                <div className="tasks-daily-table">
                  <FilterSection
                    itemList={dailyOccurrences}
                    renderFunction={renderTask}
                    filterFunction={(o: TaskOccurrence) =>
                      isTaskWithType(o, 'OpenChecklist') && o.status !== 'Completed'
                    }
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'opening' })}
                  />
                  <FilterSection
                    itemList={dailyOccurrences}
                    renderFunction={renderTask}
                    filterFunction={(o: TaskOccurrence) =>
                      isTaskWithType(o, 'CleaningList') && o.status !== 'Completed'
                    }
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'daily' })}
                  />
                  <FilterSection
                    itemList={dailyOccurrences}
                    renderFunction={renderTask}
                    filterFunction={(o: TaskOccurrence) =>
                      isTaskWithType(o, 'ClosingChecklist') && o.status !== 'Completed'
                    }
                    sortFunction={sortByName}
                    title={intl.formatMessage({ id: 'closing' })}
                  />
                  {hasInteriorTasks(dailyOccurrences) && (
                    <FilterSection
                      itemList={dailyOccurrences}
                      renderFunction={renderTask}
                      filterFunction={(o: TaskOccurrence) =>
                        isTaskWithType(o, 'ExpressInterior') && o.status !== 'Completed'
                      }
                      sortFunction={sortByName}
                      title={intl.formatMessage({ id: 'interior' })}
                      isOpen={false}
                    />
                  )}
                  {/* Completed tasks */}
                  <FilterSection
                    itemList={dailyOccurrences}
                    renderFunction={renderTask}
                    filterFunction={(o: TaskOccurrence) => o.status === 'Completed'}
                    sortFunction={sortByCompleted}
                    title={intl.formatMessage({ id: 'completed' })}
                    isOpen={false}
                  />
                </div>
              )}
            </div>
            <HourlyChecklist
              hourlyTasks={hourlyOccurrences}
              lastFourHoursTasks={lastFourHoursTasks}
              filterComplete={filterComplete}
              viewMode={hourlyViewMode}
              viewModeChange={setHourlyViewMode}
              setSelectedTask={(occ: TaskOccurrence | null | undefined) =>
                setSelectedOccurrence(occ ?? null)
              }
              getTaskName={(occ: TaskOccurrence) => occ?.task?.name || occ?.name || ''}
              renderItem={(occ: TaskOccurrence, isCompleted: boolean, i: number) => (
                <TaskOccurrenceCard
                  key={`hourly-${isCompleted ? 'c' : 'i'}-${occ.id}-${i}`}
                  occurrence={occ}
                  onOpen={(o) => setSelectedOccurrence(o)}
                  className="tabular"
                />
              )}
            />
          </div>

          <Drawer
            expanded={!!selectedOccurrence}
            maximized={false}
            expandedWidth={TASK_DRAWER_WIDTH}
            overlay
            onBackdropClick={() => setSelectedOccurrence(null)}
          >
            {selectedOccurrence && (
              <TaskDetails
                template={selectedTemplate ?? null}
                onClose={() => setSelectedOccurrence(null)}
                onSave={() => setSelectedOccurrence(null)}
              />
            )}
          </Drawer>
        </>
      )}
    </div>
  );
};

export default TasksV2Daily;
